import { Zap } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { ASSETS } from '@/assets';
import { PetPeek } from '@/components/PetPeek';
import { PetBackdrop } from '@/components/PetBackdrop';

export const Hero = () => {
  const { t } = useLanguage();
  const stats = [
    { v: t.hero.stat1v, l: t.hero.stat1l, orange: true },
    { v: t.hero.stat2v, l: t.hero.stat2l, orange: false },
    { v: t.hero.stat3v, l: t.hero.stat3l, orange: true },
  ];
  const words = t.hero.title.split(' ');
  const lastWord = words.pop();
  return (
    <section id="hero" className="relative overflow-hidden">
      <PetBackdrop src={ASSETS.petsTrio} className="right-[-80px] bottom-[-60px] w-[600px] h-[600px] hidden lg:block" opacity={0.16} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">

        <div className="lg:col-span-7 space-y-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange animate-fade-up">{t.hero.overline}</p>
          <h1 className="font-display text-6xl md:text-[7.5vw] lg:text-[6.2vw] uppercase leading-[0.85] tracking-tight text-brand-green animate-fade-up" style={{ animationDelay: '80ms' }}>
            {words.join(' ')}{' '}
            <span className="text-brand-orange">{lastWord}</span>
          </h1>
          <p className="text-base md:text-lg font-medium leading-relaxed text-brand-green/80 max-w-xl animate-fade-up" style={{ animationDelay: '160ms' }}>
            {t.hero.subtitle}
          </p>

          <div className="flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: '240ms' }}>
            {stats.map((s) => (
              <div key={s.l} className="px-5 py-3 bg-brand-bone/85 border border-brand-green/15 rounded-2xl shadow-sm">
                <span className={`block text-2xl font-display leading-none ${s.orange ? 'text-brand-orange' : 'text-brand-green'}`}>{s.v}</span>
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-brand-green/60">{s.l}</span>
              </div>
            ))}
          </div>

          <div className="pt-2 flex flex-col sm:flex-row gap-4 items-start sm:items-center animate-fade-up" style={{ animationDelay: '320ms' }}>
            <a
              href="#product"
              data-testid="hero-order-btn"
              className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-brand-orange text-white text-base font-extrabold uppercase tracking-wide hover:bg-[#C75007] hover:scale-[0.98] transition-all shadow-xl citrus-glow"
            >
              {t.hero.cta}
            </a>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-green/70">
              <Zap className="w-3.5 h-3.5 text-brand-orange" /> {t.hero.shipsNote}
            </span>
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: '200ms' }}>
          <PetPeek type="cat" className="top-2 left-4 z-20 hidden sm:block" />
          <img
            src={ASSETS.bottleHero}
            alt="Angry Tangerine Odor Eliminator concentrate bottle, 237 ml"
            data-testid="hero-product-img"
            className="w-full max-w-md mx-auto mix-blend-multiply relative z-10"
            style={{ filter: 'drop-shadow(0 34px 40px rgba(15, 61, 46, 0.28))' }}
            loading="eager"
          />
          <div className="absolute right-0 sm:right-2 top-1/4 rotate-3 bg-brand-bone border-2 border-brand-green/15 rounded-2xl px-4 py-2.5 shadow-xl z-20" data-testid="hero-yield-chip">
            <span className="font-display text-lg uppercase text-brand-green">{t.hero.chip}</span>
          </div>
          <PetPeek type="dog" className="bottom-4 right-6 z-20 hidden sm:block" />
        </div>
      </div>
    </section>
  );
};
